import { useMemo, useState, useCallback } from "react";
import { buildDynoSeries } from "@/lib/dynoCurves.mjs";

const STAGE_COLORS = {
  stock: "#8A99B0",
  stage1: "#F5A524",
  stage2: "#4CC3FF",
  stage3: "#FF5D5D",
};

const W = 880;
const H = 430;
const M = { t: 28, r: 64, b: 46, l: 60 };
const PW = W - M.l - M.r;
const PH = H - M.t - M.b;

function niceTicks(max, count) {
  const raw = max / count;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => raw <= s) || mag * 10;
  const ticks = [];
  for (let v = 0; v <= max * 1.001; v += step) ticks.push(v);
  return ticks;
}

function smoothPath(pts) {
  if (pts.length < 2) return "";
  let d = `M ${pts[0].x.toFixed(1)} ${pts[0].y.toFixed(1)}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[Math.max(0, i - 1)];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[Math.min(pts.length - 1, i + 2)];
    const c1x = p1.x + (p2.x - p0.x) / 6;
    const c1y = p1.y + (p2.y - p0.y) / 6;
    const c2x = p2.x - (p3.x - p1.x) / 6;
    const c2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C ${c1x.toFixed(1)} ${c1y.toFixed(1)}, ${c2x.toFixed(1)} ${c2y.toFixed(1)}, ${p2.x.toFixed(1)} ${p2.y.toFixed(1)}`;
  }
  return d;
}

/**
 * Dyno Graph — rolling-road style power & torque curves generated from the
 * vehicle's stored performance figures. Power solid / torque dashed per
 * stage; hover or touch for exact readouts; legend toggles stages.
 */
export function DynoGraph({ vehicle, testPrefix = "dg" }) {
  const model = useMemo(() => buildDynoSeries(vehicle), [vehicle]);
  const [hidden, setHidden] = useState({});
  const [hover, setHover] = useState(null); // {x (px), rpm, values:[{key,label,hp,tq,color}]}

  const geom = useMemo(() => {
    if (!model) return null;
    const { start, redline, maxBhp, maxNm } = model;
    const sx = (rpm) => M.l + ((rpm - start) / (redline - start)) * PW;
    const syP = (hp) => M.t + PH - (hp / (maxBhp * 1.06)) * PH;
    const syT = (nm) => M.t + PH - (nm / (maxNm * 1.06)) * PH;
    return { sx, syP, syT };
  }, [model]);

  const onMove = useCallback(
    (e) => {
      if (!model || !geom) return;
      const svg = e.currentTarget;
      const rect = svg.getBoundingClientRect();
      const cx = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
      const px = (cx / rect.width) * W;
      if (px < M.l || px > M.l + PW) return setHover(null);
      const rpm = Math.round(model.start + ((px - M.l) / PW) * (model.redline - model.start));
      const values = model.series
        .filter((s) => !hidden[s.key])
        .map((s) => {
          let best = s.points[0];
          for (const p of s.points) if (Math.abs(p.rpm - rpm) < Math.abs(best.rpm - rpm)) best = p;
          return { key: s.key, label: s.label, hp: best.hp, tq: best.tq, color: STAGE_COLORS[s.key] };
        });
      setHover({ x: px, rpm, values });
    },
    [model, geom, hidden]
  );

  if (!model || !geom) return null;
  const { sx, syP, syT } = geom;
  const { start, redline, maxBhp, maxNm, series } = model;
  const visible = series.filter((s) => !hidden[s.key]);
  const rpmTicks = [];
  const rpmStep = redline - start > 3000 ? 1000 : 500;
  for (let r = Math.ceil(start / rpmStep) * rpmStep; r <= redline; r += rpmStep) rpmTicks.push(r);
  const bhpTicks = niceTicks(maxBhp * 1.06, 5);
  const nmTicks = niceTicks(maxNm * 1.06, 5);

  return (
    <section className="mt-10 border border-line" data-testid={`${testPrefix}-dyno-graph`}>
      <div className="border-b border-line bg-surface px-6 py-5">
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-regal-amber">Rolling road</p>
        <h2 className="mt-1 font-display text-2xl font-bold uppercase tracking-[0.04em] text-ink sm:text-3xl">Dyno Graph</h2>
        <p className="mt-1 text-sm text-neutral-500">
          Power (BHP, solid) and torque (Nm, dashed) across engine speed (RPM). Peak figures match our database exactly.
        </p>
      </div>

      <div className="bg-[#0B111C] p-3 sm:p-5">
        {/* legend / toggles */}
        <div className="mb-3 flex flex-wrap items-center gap-2 px-1" data-testid={`${testPrefix}-dyno-legend`}>
          {series.map((s) => (
            <button
              key={s.key}
              type="button"
              onClick={() => setHidden((h) => ({ ...h, [s.key]: !h[s.key] }))}
              data-testid={`${testPrefix}-dyno-legend-${s.key}`}
              className={`flex items-center gap-2 border px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.14em] transition-opacity ${
                hidden[s.key] ? "border-white/15 text-white/35 opacity-60" : "border-white/20 text-white"
              }`}
              style={{ background: hidden[s.key] ? "transparent" : "rgba(255,255,255,0.06)" }}
            >
              <span className="flex flex-col gap-[3px]">
                <span className="block h-[2px] w-4" style={{ background: STAGE_COLORS[s.key] }} />
                <span className="block h-0 w-4 border-t border-dashed" style={{ borderColor: STAGE_COLORS[s.key] }} />
              </span>
              {s.label}
            </button>
          ))}
          <span className="ml-auto hidden font-mono text-[10px] uppercase tracking-[0.14em] text-white/40 sm:block">
            — Power (BHP)&nbsp;&nbsp;- - Torque (Nm)
          </span>
        </div>

        <div className="relative">
          <svg
            viewBox={`0 0 ${W} ${H}`}
            className="h-auto w-full select-none"
            data-testid={`${testPrefix}-dyno-svg`}
            onMouseMove={onMove}
            onMouseLeave={() => setHover(null)}
            onTouchMove={onMove}
            onTouchEnd={() => setHover(null)}
            role="img"
            aria-label={`Dyno graph: power and torque curves for ${series.map((s) => s.label).join(", ")}`}
          >
            {/* grid */}
            {bhpTicks.map((t) => (
              <g key={`g${t}`}>
                <line x1={M.l} x2={M.l + PW} y1={syP(t)} y2={syP(t)} stroke="#1C2735" strokeWidth="1" />
                <text x={M.l - 8} y={syP(t) + 3} textAnchor="end" fontSize="10" fill="#7C8DA6" fontFamily="monospace">{t}</text>
              </g>
            ))}
            {nmTicks.map((t) => (
              <text key={`r${t}`} x={M.l + PW + 8} y={syT(t) + 3} textAnchor="start" fontSize="10" fill="#5E7A8A" fontFamily="monospace">{t}</text>
            ))}
            {rpmTicks.map((r) => (
              <g key={`x${r}`}>
                <line x1={sx(r)} x2={sx(r)} y1={M.t} y2={M.t + PH} stroke="#141D29" strokeWidth="1" />
                <text x={sx(r)} y={M.t + PH + 18} textAnchor="middle" fontSize="10" fill="#7C8DA6" fontFamily="monospace">
                  {r >= 1000 ? `${(r / 1000).toFixed(r % 1000 ? 1 : 0)}k` : r}
                </text>
              </g>
            ))}
            {/* frame + axis titles */}
            <rect x={M.l} y={M.t} width={PW} height={PH} fill="none" stroke="#2A3A4E" strokeWidth="1" />
            <text x={M.l - 44} y={M.t - 10} fontSize="10" fill="#9FB2C8" fontFamily="monospace" letterSpacing="2">BHP</text>
            <text x={M.l + PW + 46} y={M.t - 10} textAnchor="end" fontSize="10" fill="#7FA3B5" fontFamily="monospace" letterSpacing="2">Nm</text>
            <text x={M.l + PW / 2} y={H - 8} textAnchor="middle" fontSize="10" fill="#9FB2C8" fontFamily="monospace" letterSpacing="3">RPM</text>

            {/* curves */}
            {visible.map((s) => (
              <g key={s.key}>
                <path
                  d={smoothPath(s.points.map((p) => ({ x: sx(p.rpm), y: syP(p.hp) })))}
                  fill="none" stroke={STAGE_COLORS[s.key]} strokeWidth="2.4" strokeLinecap="round"
                  data-testid={`${testPrefix}-dyno-curve-${s.key}-power`}
                />
                <path
                  d={smoothPath(s.points.map((p) => ({ x: sx(p.rpm), y: syT(p.tq) })))}
                  fill="none" stroke={STAGE_COLORS[s.key]} strokeWidth="1.6" strokeDasharray="6 4" opacity="0.85"
                  data-testid={`${testPrefix}-dyno-curve-${s.key}-torque`}
                />
                {/* peak markers */}
                <circle cx={sx(s.hpPeak.rpm)} cy={syP(s.hpPeak.hp)} r="3.4" fill={STAGE_COLORS[s.key]} data-testid={`${testPrefix}-dyno-peak-${s.key}-power`} />
                <circle cx={sx(s.tqPeak.rpm)} cy={syT(s.tqPeak.tq)} r="3" fill="none" stroke={STAGE_COLORS[s.key]} strokeWidth="1.6" />
                <text x={sx(s.hpPeak.rpm)} y={syP(s.hpPeak.hp) - 8} textAnchor="middle" fontSize="10" fill={STAGE_COLORS[s.key]} fontFamily="monospace">
                  {s.hpPeak.hp} bhp
                </text>
              </g>
            ))}

            {/* crosshair */}
            {hover && (
              <line x1={hover.x} x2={hover.x} y1={M.t} y2={M.t + PH} stroke="#FFFFFF" strokeWidth="1" opacity="0.35" data-testid={`${testPrefix}-dyno-crosshair`} />
            )}
          </svg>

          {/* readout */}
          <div
            className="pointer-events-none absolute right-2 top-2 border border-white/15 bg-black/70 px-3 py-2 backdrop-blur-sm"
            data-testid={`${testPrefix}-dyno-readout`}
          >
            <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-white/50">
              {hover ? `${hover.rpm.toLocaleString()} rpm` : "Hover / touch the graph"}
            </p>
            {(hover ? hover.values : visible.map((s) => ({ key: s.key, label: s.label, hp: s.hpPeak.hp, tq: s.tqPeak.tq, color: STAGE_COLORS[s.key], peak: true }))).map((v) => (
              <p key={v.key} className="mt-0.5 font-mono text-[11px]" style={{ color: v.color }}>
                {v.label}: {v.hp} bhp · {v.tq} Nm{v.peak ? " (peak)" : ""}
              </p>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
